import { useState, useEffect } from 'react';

export const USE_MOCK = false;
export const PER_PAGE = 20;

export const STATUS_MAP = {
  received:    { variant: 'info',      labelKey: 'invoices.status_received'    },
  processing:  { variant: 'preparing', labelKey: 'invoices.status_processing'  },
  sent_to_erp: { variant: 'paid',      labelKey: 'invoices.status_sent_to_erp' },
  rejected:    { variant: 'disputed',  labelKey: 'invoices.status_rejected'    },
};

export const MISSING_FIELD_LABEL_KEYS = {
  invoiceNo: 'invoices.col_invoice_number',
  salesOrders: 'invoices.col_sales_order_no',
  billOfLadingNo: 'invoices.col_bill_of_lading',
  containers: 'invoices.col_container_no',
  vesselName: 'invoices.col_vessel',
  postingDate: 'invoices.col_posting_date',
};

export const EMPTY_ADV = {
  supplier: '',
  invoiceDateFrom: '', invoiceDateTo: '',
  dueDateFrom: '', dueDateTo: '',
  erpSentFrom: '', erpSentTo: '',
  statuses: [],
  lateOnly: false,
  incompleteOnly: false,
};

export const DEFAULT_RECEIVED_RANGE_DAYS = 120;

// The always-visible "received date" range control defaults to the last
// N days so the invoices list isn't unbounded (and thus slow / overwhelming)
// on first load.
export function getDefaultReceivedRange(days = DEFAULT_RECEIVED_RANGE_DAYS) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { from: fmt(from), to: fmt(to) };
}

export function fmtDate(d, locale) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(locale === 'en' ? 'en-GB' : 'fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

export function fmtAmount(amount, currencyCode) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currencyCode || 'MAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function fmtAmountShort(amount, currencyCode) {
  if (amount == null) return '—';
  if (Math.abs(amount) < 1_000) return fmtAmount(amount, currencyCode);
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currencyCode || 'MAD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(amount);
}

// Converts an amount in `currencyCode` to MAD using that invoice's own
// exchangeRate — MAD amounts need no conversion. Returns null when the rate
// isn't known.
export function convertToMAD(amount, currencyCode, exchangeRate) {
  if (amount == null) return null;
  if (!currencyCode || currencyCode === 'MAD') return amount;
  return exchangeRate != null ? amount * exchangeRate : null;
}

export function useDebounce(value, ms = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function isOverdue(row) {
  const dueDate = typeof row.dueDate === 'string' ? row.dueDate.trim() : '';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return false;
  const parsed = new Date(`${dueDate}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== dueDate) return false;
  return dueDate < new Date().toISOString().slice(0, 10) && row.status !== 'sent_to_erp';
}

export function isLate(row, thresholdDays = 7) {
  if (!row.postingDate || !row.receivedAt) return false;
  const posting = new Date(row.postingDate);
  const received = new Date(row.receivedAt);
  const postingUTCDate = Date.UTC(posting.getUTCFullYear(), posting.getUTCMonth(), posting.getUTCDate());
  const receivedUTCDate = Date.UTC(received.getUTCFullYear(), received.getUTCMonth(), received.getUTCDate());
  return Math.round((receivedUTCDate - postingUTCDate) / 86400000) > thresholdDays;
}

export function countActiveFilters(adv) {
  let n = 0;
  if (adv.supplier.trim()) n++;
  if (adv.invoiceDateFrom || adv.invoiceDateTo) n++;
  if (adv.dueDateFrom || adv.dueDateTo) n++;
  if (adv.erpSentFrom || adv.erpSentTo) n++;
  if (adv.statuses.length) n++;
  if (adv.lateOnly) n++;
  if (adv.incompleteOnly) n++;
  return n;
}
